import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Plus, Trash2, Upload, Eye, EyeOff, Play, Video, FolderOpen, ArrowLeft, Check, Image
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks for faster large uploads

const FreeVideoManagement = ({ sessionToken }) => {
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: '', duration_seconds: 0 });
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [newCatName, setNewCatName] = useState('');
  const [showCats, setShowCats] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/free-videos/admin/all?session_token=${sessionToken}`);
      if (res.ok) {
        const data = await res.json();
        setVideos(data.videos || []);
        setCategories(data.categories || []);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const createVideo = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/free-videos/admin/create?session_token=${sessionToken}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        setShowForm(false);
        setForm({ title: '', description: '', category: '', duration_seconds: 0 });
        fetchData();
      }
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const togglePublish = async (video) => {
    await fetch(`${API_URL}/api/free-videos/admin/${video.id}?session_token=${sessionToken}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_published: !video.is_published })
    });
    fetchData();
  };

  const deleteVideo = async (id) => {
    if (!window.confirm('Delete this video?')) return;
    await fetch(`${API_URL}/api/free-videos/admin/${id}?session_token=${sessionToken}`, { method: 'DELETE' });
    fetchData();
  };

  const uploadVideoFile = async (videoId, file) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    setUploadProgress(p => ({ ...p, [videoId]: { percent: 0, status: 'uploading' } }));

    // Init
    const initRes = await fetch(`${API_URL}/api/free-videos/admin/upload/init?session_token=${sessionToken}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: file.name, content_type: file.type, total_chunks: totalChunks, video_id: videoId })
    });
    const { upload_id } = await initRes.json();

    // Upload chunks
    for (let i = 0; i < totalChunks; i++) {
      const chunk = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
      const fd = new FormData();
      fd.append('upload_id', upload_id);
      fd.append('chunk_index', i);
      fd.append('session_token', sessionToken);
      fd.append('file', chunk);
      await fetch(`${API_URL}/api/free-videos/admin/upload/chunk`, { method: 'POST', body: fd });
      setUploadProgress(p => ({ ...p, [videoId]: { percent: Math.round(((i + 1) / totalChunks) * 100), status: 'uploading' } }));
    }

    // Complete
    await fetch(`${API_URL}/api/free-videos/admin/upload/complete?session_token=${sessionToken}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ upload_id, video_id: videoId })
    });
    setUploadProgress(p => ({ ...p, [videoId]: { percent: 100, status: 'done' } }));
    fetchData();
  };

  const createCategory = async () => {
    if (!newCatName.trim()) return;
    await fetch(`${API_URL}/api/free-videos/admin/categories?session_token=${sessionToken}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newCatName.trim() })
    });
    setNewCatName('');
    fetchData();
  };

  const deleteCategory = async (id) => {
    await fetch(`${API_URL}/api/free-videos/admin/categories/${id}?session_token=${sessionToken}`, { method: 'DELETE' });
    fetchData();
  };

  const uploadThumbnail = async (videoId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/free-videos/admin/upload/thumbnail/${videoId}?session_token=${sessionToken}`, {
        method: 'POST', body: fd
      });
      if (res.ok) fetchData();
    } catch (err) { console.error('Thumbnail upload failed:', err); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="free-video-management">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Free Learning Videos ({videos.length})</h2>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowCats(!showCats)} data-testid="manage-free-cats-btn">
            <FolderOpen className="w-4 h-4 mr-1" /> Categories
          </Button>
          <Button size="sm" onClick={() => setShowForm(!showForm)} data-testid="add-free-video-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Video
          </Button>
        </div>
      </div>

      {/* Category Management */}
      {showCats && (
        <Card className="bg-gray-50">
          <CardContent className="p-4">
            <h3 className="font-medium mb-3">Video Categories</h3>
            <div className="space-y-2 mb-3">
              {categories.map(c => (
                <div key={c.id} className="flex items-center justify-between bg-white p-2 rounded border">
                  <span>{c.name}</span>
                  <Button size="sm" variant="ghost" onClick={() => deleteCategory(c.id)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input placeholder="New category" value={newCatName} onChange={e => setNewCatName(e.target.value)} className="flex-1" />
              <Button size="sm" onClick={createCategory}>Add</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Form */}
      {showForm && (
        <Card className="bg-gray-50">
          <CardContent className="p-4 space-y-3">
            <h3 className="font-medium">New Free Video</h3>
            <Input placeholder="Video Title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="free-video-title-input" />
            <Input placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            <div className="flex gap-2">
              <select
                className="flex-1 px-3 py-2 border rounded-md text-sm"
                value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}
              >
                <option value="">No Category</option>
                {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
              <Input
                type="number" placeholder="Duration (sec)" className="w-32"
                value={form.duration_seconds || ''}
                onChange={e => setForm({ ...form, duration_seconds: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={createVideo} disabled={!form.title || saving}>
                {saving ? 'Creating...' : 'Create Video'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Video List */}
      {videos.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Video className="w-10 h-10 mx-auto mb-2 text-gray-400" />
          <p>No free videos yet. Add your first one!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {videos.map(v => (
            <Card key={v.id} className="bg-white border">
              <CardContent className="p-4">
                <div className="flex gap-4">
                  {/* Thumbnail Preview */}
                  <div className="w-28 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 relative group">
                    {v.thumbnail_url ? (
                      <img src={`${API_URL}${v.thumbnail_url}`} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <Image className="w-6 h-6" />
                      </div>
                    )}
                    <label className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                      <input type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files[0]) uploadThumbnail(v.id, e.target.files[0]); }} />
                      <span className="text-white text-[10px] font-medium flex items-center gap-1"><Image className="w-3 h-3" /> {v.thumbnail_url ? 'Change' : 'Add'}</span>
                    </label>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-gray-900 truncate">{v.title}</h3>
                      <Badge className={v.is_published ? 'bg-green-100 text-green-800 text-[10px]' : 'bg-gray-100 text-gray-600 text-[10px]'}>
                        {v.is_published ? 'Published' : 'Draft'}
                      </Badge>
                      {v.video_storage_path && (
                        <Badge className="bg-blue-100 text-blue-700 text-[10px]">
                          <Check className="w-3 h-3 mr-0.5" /> Video
                        </Badge>
                      )}
                      {v.thumbnail_url && (
                        <Badge className="bg-purple-100 text-purple-700 text-[10px]">
                          <Image className="w-3 h-3 mr-0.5" /> Thumb
                        </Badge>
                      )}
                    </div>
                    {v.description && <p className="text-sm text-gray-600 mb-1 truncate">{v.description}</p>}
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      {v.category && <span>Category: {v.category}</span>}
                      <span><Eye className="w-3 h-3 inline mr-0.5" />{v.view_count || 0} views</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {/* Video Upload */}
                    <label className="cursor-pointer">
                      <input type="file" accept="video/*" className="hidden" onChange={e => { if (e.target.files[0]) uploadVideoFile(v.id, e.target.files[0]); }} />
                      <div className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 px-2 py-1 border border-blue-200 rounded" data-testid={`upload-video-${v.id}`}>
                        <Upload className="w-3 h-3" /> Video
                      </div>
                    </label>
                    {/* Thumbnail Upload */}
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files[0]) uploadThumbnail(v.id, e.target.files[0]); }} />
                      <div className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 px-2 py-1 border border-purple-200 rounded" data-testid={`upload-thumb-${v.id}`}>
                        <Image className="w-3 h-3" /> Thumb
                      </div>
                    </label>
                    {/* Publish toggle */}
                    <Switch checked={v.is_published} onCheckedChange={() => togglePublish(v)} disabled={!v.video_storage_path} />
                    {/* Delete */}
                    <Button size="sm" variant="ghost" onClick={() => deleteVideo(v.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
                {/* Upload progress */}
                {uploadProgress[v.id] && uploadProgress[v.id].status === 'uploading' && (
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${uploadProgress[v.id].percent}%` }} />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Uploading... {uploadProgress[v.id].percent}%</p>
                  </div>
                )}
                {uploadProgress[v.id]?.status === 'done' && (
                  <p className="text-xs text-green-600 mt-2">Upload complete!</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default FreeVideoManagement;
